import * as React from 'react';
import { getPaletteColor } from '../theme';
const COMPONENT_TOKEN_PREFIX = 'components.filter-textfield';
const getComponentColor = (theme, suffix) => getPaletteColor(theme, `${COMPONENT_TOKEN_PREFIX}.${suffix}`);
const paletteToken = (token) => ({
    kind: 'palette',
    token
});
const componentToken = (suffix) => ({
    kind: 'component',
    suffix
});
const getBoxShadowValues = (width, color, inset = false) => `${inset ? 'inset ' : ''}0 0 0 ${width}px ${color}`;
const resolveColor = (theme, colorToken) => colorToken.kind === 'palette'
    ? getPaletteColor(theme, colorToken.token)
    : getComponentColor(theme, colorToken.suffix);
const DEFAULT_RING = {
    border: componentToken('focused.border'),
    glow: paletteToken('primary._states.focus-visible')
};
const STATE_CONFIG = {
    enabled: {
        color: componentToken('enabled.text'),
        backgroundColor: componentToken('enabled.bg'),
        placeholderColor: componentToken('enabled.text'),
        hover: {
            backgroundColor: componentToken('hover.bg-hover'),
            border: componentToken('hover.border')
        },
        focus: {
            backgroundColor: componentToken('focused.bg'),
            placeholderColor: componentToken('focused.placeholder'),
            ring: DEFAULT_RING
        }
    },
    filled: {
        color: componentToken('filled.value'),
        backgroundColor: componentToken('filled.bg'),
        placeholderColor: componentToken('enabled.text'),
        hover: {
            backgroundColor: componentToken('hover.bg-hover'),
            border: componentToken('hover.border')
        },
        focus: {
            backgroundColor: componentToken('focused.bg'),
            placeholderColor: componentToken('focused.placeholder'),
            ring: DEFAULT_RING
        }
    },
    focused: {
        color: componentToken('filled.value'),
        backgroundColor: componentToken('focused.bg'),
        placeholderColor: componentToken('focused.placeholder'),
        hover: {
            backgroundColor: componentToken('hover.bg-hover'),
            border: componentToken('hover.border')
        },
        focus: {
            backgroundColor: componentToken('focused.bg'),
            placeholderColor: componentToken('focused.placeholder'),
            ring: DEFAULT_RING
        }
    },
    error: {
        color: componentToken('error.value'),
        backgroundColor: componentToken('error.bg'),
        placeholderColor: componentToken('error.value'),
        baseBorder: componentToken('error.border'),
        hover: { backgroundColor: componentToken('error.bg') },
        focus: {
            backgroundColor: componentToken('focused.bg'),
            placeholderColor: componentToken('error.value'),
            ring: {
                border: componentToken('error.border'),
                glow: paletteToken('error._states.focus-visible')
            }
        }
    },
    disabled: {
        color: paletteToken('text.disabled'),
        backgroundColor: componentToken('enabled.bg'),
        placeholderColor: paletteToken('text.disabled'),
        hover: { backgroundColor: componentToken('enabled.bg') },
        focus: {
            backgroundColor: componentToken('focused.bg'),
            placeholderColor: componentToken('focused.placeholder')
        }
    }
};
export function getFilterTextFieldStyles(theme, state) {
    const stateConfig = STATE_CONFIG[state];
    const focusRing = stateConfig.focus.ring;
    return {
        color: resolveColor(theme, stateConfig.color),
        backgroundColor: resolveColor(theme, stateConfig.backgroundColor),
        placeholderColor: resolveColor(theme, stateConfig.placeholderColor),
        boxShadow: stateConfig.baseBorder
            ? getBoxShadowValues(1, resolveColor(theme, stateConfig.baseBorder), true)
            : 'none',
        hover: {
            backgroundColor: resolveColor(theme, stateConfig.hover.backgroundColor),
            boxShadow: stateConfig.hover.border
                ? getBoxShadowValues(1, resolveColor(theme, stateConfig.hover.border))
                : undefined
        },
        focus: {
            backgroundColor: resolveColor(theme, stateConfig.focus.backgroundColor),
            placeholderColor: resolveColor(theme, stateConfig.focus.placeholderColor),
            boxShadow: focusRing
                ? [
                    getBoxShadowValues(1, resolveColor(theme, focusRing.border), true),
                    getBoxShadowValues(6, resolveColor(theme, focusRing.glow))
                ].join(', ')
                : 'none'
        }
    };
}
export function useFontLoadRemeasure() {
    const [version, setVersion] = React.useState(0);
    React.useEffect(() => {
        const fontSet = document.fonts;
        if (!fontSet) {
            return;
        }
        let active = true;
        const remeasure = () => {
            if (active) {
                setVersion((currentVersion) => currentVersion + 1);
            }
        };
        const waitForFonts = async () => {
            await fontSet.ready;
            remeasure();
        };
        void waitForFonts();
        fontSet.addEventListener('loadingdone', remeasure);
        fontSet.addEventListener('loadingerror', remeasure);
        return () => {
            active = false;
            fontSet.removeEventListener('loadingdone', remeasure);
            fontSet.removeEventListener('loadingerror', remeasure);
        };
    }, []);
    return version;
}
export function getResolvedState(props) {
    const { error, disabled, focused, hasValue } = props;
    if (disabled) {
        return 'disabled';
    }
    if (error) {
        return 'error';
    }
    if (focused) {
        return 'focused';
    }
    if (hasValue) {
        return 'filled';
    }
    return 'enabled';
}
